import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Button,
    TextField,
    MenuItem,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Warning as WarningIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import { mlAnalyticsAPI, DropoutRiskPrediction } from '../../services/mlAnalyticsApi';

const RISK_COLORS = {
    critical: '#d32f2f',
    high: '#f57c00',
    medium: '#fbc02d',
    low: '#388e3c',
};

const DropoutRiskDashboard: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dashboardData, setDashboardData] = useState<any>(null);
    const [atRiskStudents, setAtRiskStudents] = useState<DropoutRiskPrediction[]>([]);
    const [selectedRiskLevel, setSelectedRiskLevel] = useState<string>('all');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        if (selectedRiskLevel !== 'all') {
            fetchAtRiskStudents(selectedRiskLevel);
        } else {
            fetchAtRiskStudents();
        }
    }, [selectedRiskLevel]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await mlAnalyticsAPI.getDashboard();

            if (response.data.status === 'success') {
                setDashboardData(response.data.data);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err: any) {
            console.error('Dashboard error:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    const fetchAtRiskStudents = async (riskLevel?: string) => {
        try {
            const params = riskLevel && riskLevel !== 'all' ? { risk_level: riskLevel } : {};
            const response = await mlAnalyticsAPI.getAtRiskStudents(params);

            if (response.data.status === 'success') {
                setAtRiskStudents(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching at-risk students:', err);
        }
    };

    const getRiskColor = (level: string) => {
        return RISK_COLORS[level as keyof typeof RISK_COLORS] || '#757575';
    };

    const getRiskChipColor = (level: string): any => {
        switch (level) {
            case 'critical':
                return 'error';
            case 'high':
                return 'warning';
            case 'medium':
                return 'info';
            case 'low':
                return 'success';
            default:
                return 'default';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={fetchDashboardData} startIcon={<RefreshIcon />}>
                    Retry
                </Button>
            </Box>
        );
    }

    // Prepare pie chart data
    const pieData = dashboardData?.risk_distribution?.map((item: any) => ({
        name: item.risk_level,
        value: item.count,
        color: getRiskColor(item.risk_level),
    })) || [];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Dropout Risk Prediction
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        AI-powered early warning system for student dropout prevention
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchDashboardData}
                >
                    Refresh
                </Button>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <WarningIcon color="error" sx={{ mr: 1 }} />
                                <Typography variant="h6">Critical Risk</Typography>
                            </Box>
                            <Typography variant="h3" color="error">
                                {dashboardData?.critical_cases || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Immediate action required
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                                <Typography variant="h6">High Risk</Typography>
                            </Box>
                            <Typography variant="h3" color="warning.main">
                                {dashboardData?.high_risk_cases || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Intervention recommended
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Total At-Risk</Typography>
                            </Box>
                            <Typography variant="h3" color="primary">
                                {dashboardData?.total_at_risk || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Students needing support
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={1}>
                                <AssignmentIcon color="info" sx={{ mr: 1 }} />
                                <Typography variant="h6">Interventions</Typography>
                            </Box>
                            <Typography variant="h3" color="info.main">
                                {dashboardData?.intervention_stats?.reduce((sum: number, item: any) => sum + item.count, 0) || 0}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Active interventions
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Risk Distribution Chart */}
                {pieData.length > 0 && (
                    <Grid item xs={12} md={5}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Risk Level Distribution
                                </Typography>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, value }) => `${name}: ${value}`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {pieData.map((entry: any, index: number) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Intervention Status */}
                <Grid item xs={12} md={7}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Intervention Status
                            </Typography>
                            {dashboardData?.intervention_stats?.length > 0 ? (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="right">Count</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dashboardData.intervention_stats.map((item: any, index: number) => (
                                                <TableRow key={index}>
                                                    <TableCell sx={{ textTransform: 'capitalize' }}>{item.status}</TableCell>
                                                    <TableCell align="right">{item.count}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    No interventions yet
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* At-Risk Students List */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    At-Risk Students
                                </Typography>
                                <TextField
                                    select
                                    size="small"
                                    value={selectedRiskLevel}
                                    onChange={(e) => setSelectedRiskLevel(e.target.value)}
                                    sx={{ minWidth: 150 }}
                                >
                                    <MenuItem value="all">All Levels</MenuItem>
                                    <MenuItem value="critical">Critical</MenuItem>
                                    <MenuItem value="high">High</MenuItem>
                                    <MenuItem value="medium">Medium</MenuItem>
                                    <MenuItem value="low">Low</MenuItem>
                                </TextField>
                            </Box>

                            {atRiskStudents.length > 0 ? (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Student Name</TableCell>
                                                <TableCell>School</TableCell>
                                                <TableCell>Risk Score</TableCell>
                                                <TableCell>Risk Level</TableCell>
                                                <TableCell>Confidence</TableCell>
                                                <TableCell>Prediction Date</TableCell>
                                                <TableCell>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {atRiskStudents.map((student) => (
                                                <TableRow key={student.id} hover>
                                                    <TableCell>{student.student_name}</TableCell>
                                                    <TableCell>{student.school_name}</TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="bold"
                                                            color={getRiskColor(student.risk_level)}
                                                        >
                                                            {student.risk_score.toFixed(1)}%
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={student.risk_level.toUpperCase()}
                                                            color={getRiskChipColor(student.risk_level)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {(student.confidence_score * 100).toFixed(0)}%
                                                    </TableCell>
                                                    <TableCell>
                                                        {new Date(student.prediction_date).toLocaleDateString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => {
                                                                    // Navigate to student detail
                                                                    window.location.href = `/ml-analytics/student/${student.student_id}`;
                                                                }}
                                                            >
                                                                <ViewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info">
                                    No at-risk students found. This is good news!
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DropoutRiskDashboard;
